import { useEffect } from "react";

/**
 * Invoke `onEscape` whenever the Escape key is pressed while `enabled` is true.
 *
 * The one-truth "close this overlay on Escape" behaviour shared by modal dialogs
 * and popovers (ConnectDialog, …): a window `keydown` listener that calls
 * `e.preventDefault()` + `onEscape()` on `key === "Escape"`, attached only while
 * enabled and torn down on disable/unmount. `enabled` defaults to `true` for
 * always-mounted-while-visible callers; pass an `open` flag for components that
 * stay mounted but render conditionally.
 *
 * CAPTURE PHASE + `stopImmediatePropagation`: the listener is registered in the
 * CAPTURE phase (`addEventListener(..., true)`) so a modal's Escape fires BEFORE
 * the app-level editor shortcut handler. The app binds a BUBBLE-phase window
 * `keydown` (`useKeyboardShortcuts.ts`, `window.addEventListener("keydown", …)`
 * with no capture flag) that, on Escape, cancels the active command / clears the
 * selection / clears the draft. A bubble-phase listener here would register LATER
 * (when the dialog opens) and therefore run AFTER the app handler — so closing
 * the modal would perturb drawing state. By capturing AND calling
 * `e.stopImmediatePropagation()` we intercept Escape first and stop it from ever
 * reaching the editor path: connection chrome closing must NEVER clear the user's
 * selection / draft / command.
 */
export function useEscapeKey(onEscape: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        // Intercept the modal's Escape AHEAD of (and prevent it from reaching)
        // the app's bubble-phase editor shortcut handler.
        e.preventDefault();
        e.stopImmediatePropagation();
        onEscape();
      }
    }
    // Capture phase (`true`): fires before the app's bubble-phase global handler.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, onEscape]);
}
